import {
  DAILY_LEVEL,
  DAILY_SIZE,
  SUDOKU_LEVELS,
  SUDOKU_SIZES,
  type SudokuLevel,
  type SudokuMode,
  type SudokuSize,
} from "@bing/engine-sudoku";
import type { SudokuGame as Game, SudokuOverview, SudokuRecordKey } from "@bing/shared";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiError } from "../../api/client";
import { Button } from "../../components/Button";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { SegmentedChoice } from "../../components/SegmentedChoice";
import { StartScreenShell } from "../../components/StartScreenShell";
import { faDigits } from "../../i18n/digits";
import { fa } from "../../i18n/fa";
import { useSession } from "../../store/context";
import { Toggle } from "../../screens/GameSettings";
import { takeResumeIntent } from "../fresh-solo";
import { formatPlayTime } from "./clock";
import { conflictGame, KeptFinishes, type SudokuDeviceStorage } from "./sync";
import { SudokuTable } from "./SudokuTable";

/**
 * «سودوکو» — THE GAME'S CONTAINER: the start screen (the unfinished game to continue, a new game by
 * size and level, «سودوکوی روز», the best times), the play screen, and the refusal when the platform
 * stops a NEW game.
 *
 * ⚠️ THE PLATFORM'S GATES STOP NEW GAMES ONLY. Ban, the mandatory channels and maintenance refuse the
 * two requests that START a game (`new`, `daily`) at the api; reading, saving and finishing a game in
 * progress pass them, so a game started before a gate came down always finishes. «بزودی» has no
 * switch for Sudoku (no `GameConfig` row).
 *
 * The start screen makes ONE read when it opens and runs nothing after it — no timer, no polling.
 * Before it, two things that must land first: what the play screen sent as it went (else the read
 * could beat that save, and «ادامه» would resume an older rev), and a finish still kept on the
 * device (a game solved with no network — `KeptFinishes`).
 *
 * THREE CARDS (the operator, 2026-11-03): the unfinished game and «سودوکوی روز» are each a card of
 * their own ABOVE the settings — the whole card is the tap that continues (the lobby's «ادامه بازی»
 * card, its look and its gold badge), so neither can be read as one of the settings — and the
 * settings card is «بازی جدید»: the RECORD for the chosen size and level first, at first sight (the
 * operator, 2026-11-03: big gold numerals — the `numeral-xl` token — with what it is the record of;
 * none yet, said plainly — it follows the pickers), then size, level, «شروع». The daily's card always
 * names its size and level.
 *
 * ⚠️ ALL THREE FIT 307×472 (Telegram Desktop) in every state, with «شروع» on screen: 396 px there.
 *
 * «بررسی اشتباه» (mistake checking, the operator 2026-11-04) is a switch kept ON THE PHONE, on by
 * default, beside the size picker — its name over the settings screen's pill switch, as tall as the
 * picker (a row of its own would not fit 307×472). It applies to the NEXT
 * game only: each game stores its own mode as it starts, the daily too. The daily's card tells
 * today's state at first sight — to start, to continue, ✓ solved with its time, or LOST to 3
 * mistakes: over for today, a new puzzle tomorrow (never startable again today).
 */

/** A card of its own (`StartScreenShell`'s `before`) — the lobby's «ادامه بازی» card: felt, gold rim;
 *  `.sudoku-card`: the app's font (a `<button>` does not inherit it) and the settings card's entrance. */
const CARD =
  "sudoku-card flex w-full items-center justify-between gap-3 rounded-xl border border-solid border-gold-deep/40 bg-gradient-to-br from-felt-deep to-felt-edge px-3 py-2 text-start shadow-[0_8px_28px_rgba(0,0,0,0.37)]";
/** …when the whole card is the tap (as the lobby's). */
const CARD_TAP =
  "m-0 appearance-none cursor-pointer transition-transform active:scale-95 hover:border-gold-deep/60 disabled:cursor-default disabled:opacity-60";
/** The lobby card's «ادامه» badge. */
const BADGE =
  "flex flex-none items-center rounded-full bg-gold-deep px-2.5 py-1 font-caption text-caption text-ink-on-gold shadow-sm";

/** «بررسی اشتباه» on this phone (absent: on, the default). */
const CHECK_KEY = "bing:sudokuCheck";

function readMode(storage: SudokuDeviceStorage): SudokuMode {
  try {
    return storage.getItem(CHECK_KEY) === "off" ? "free" : "check";
  } catch {
    return "check";
  }
}

/** `localStorage`, or nothing at all where the WebView refuses it (as «مار»'s). */
function deviceStorage(): SudokuDeviceStorage {
  try {
    if (typeof localStorage !== "undefined") return localStorage;
  } catch {
    // storage blocked (private mode)
  }
  return { getItem: () => null, setItem: () => {}, removeItem: () => {} };
}

const SIZE_OPTIONS = SUDOKU_SIZES.map((s) => [s, fa.sudoku.sizes[s]] as const);
const LEVEL_OPTIONS = SUDOKU_LEVELS.map((l) => [l, fa.sudoku.levels[l]] as const);

/** The api's refusal of a NEW game — the platform's gates — or null for anything else. */
function gateRefusal(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.code === "BANNED") return fa.errors.BANNED;
  if (error.code === "MAINTENANCE") return error.message || fa.errors.MAINTENANCE;
  if (error.code === "CHANNELS_REQUIRED") return fa.errors.CHANNELS_REQUIRED;
  return null;
}

const time = (ms: number): string => faDigits(formatPlayTime(ms));

export function SudokuGame(): ReactNode {
  const setView = useSession((s) => s.setView);
  const api = useSession((s) => s.api);
  const owner = useSession((s) => s.profile?.id ?? "anon");
  const kept = useMemo(() => new KeptFinishes(deviceStorage(), owner), [owner]);
  /** The NEXT game's mode — the phone's setting. */
  const [mode, setMode] = useState<SudokuMode>(() => readMode(deviceStorage()));
  const changeMode = (check: boolean): void => {
    const next: SudokuMode = check ? "check" : "free";
    setMode(next);
    try {
      deviceStorage().setItem(CHECK_KEY, check ? "on" : "off");
    } catch {
      // storage refused: the setting holds for this visit
    }
  };

  // ⚠️ EVERY SOLO GAME MUST CONSUME `takeResumeIntent()` — Sudoku resumes from the server, so it only
  // clears the flag (left set, the NEXT game would skip its picker).
  const [screen, setScreen] = useState<"start" | "play" | "refused">(() => {
    takeResumeIntent();
    return "start";
  });
  const [overview, setOverview] = useState<SudokuOverview | null>(null);
  const [failed, setFailed] = useState(false);
  const [game, setGame] = useState<Game | null>(null);
  const [size, setSize] = useState<SudokuSize>(9);
  const [level, setLevel] = useState<SudokuLevel>("easy");
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  /** A start that got no verdict (the network, a 5xx) — said on the control that was tapped. */
  const [startFailed, setStartFailed] = useState<"new" | "daily" | null>(null);
  /** What the play screen still sends as it goes (`SudokuTable`'s `onClosing`). */
  const closing = useRef<Promise<void>>(Promise.resolve());

  const load = useCallback((): void => {
    setFailed(false);
    void (async () => {
      await closing.current;
      await kept.sendAll(api);
      try {
        setOverview(await api.getSudoku());
      } catch {
        setFailed(true);
      }
    })();
  }, [api, kept]);

  useEffect(() => {
    if (screen === "start") load();
  }, [screen, load]);

  const toLobby = (): void => setView("lobby");

  const play = (g: Game): void => {
    setGame(g);
    setScreen("play");
  };

  /** A request that starts a game: a gate's refusal shows it; a 409 shows the game it found; no
   *  verdict says so on the control that was tapped. */
  const starting = async (
    kind: "new" | "daily",
    request: () => Promise<{ game: Game }>,
  ): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setStartFailed(null);
    try {
      play((await request()).game);
    } catch (error) {
      const refused = gateRefusal(error);
      if (refused) {
        setRefusal(refused);
        setScreen("refused");
        return;
      }
      const theirs = conflictGame(error);
      if (theirs !== undefined) {
        // Another phone left an unfinished game this one did not know of: show it, ask again.
        setOverview((o) => (o ? { ...o, classic: theirs } : o));
        return;
      }
      setStartFailed(kind);
      load();
    } finally {
      setBusy(false);
    }
  };

  const newGame = (replaces: number | null): void => {
    void starting("new", () => api.newSudokuGame({ size, level, replaces, mode }));
  };

  const daily = (): void => {
    const today = overview?.daily ?? null;
    if (today && today.finishedMs === null && today.outcome !== "lost") play(today);
    else if (!today) void starting("daily", () => api.startSudokuDaily({ mode }));
  };

  if (screen === "play" && game) {
    return (
      <SudokuTable
        key={`${game.slot}:${game.puzzle}`}
        game={game}
        api={api}
        kept={kept}
        onExit={() => {
          setGame(null);
          setScreen("start");
        }}
        onLeave={toLobby}
        onClosing={(p) => {
          closing.current = p;
        }}
      />
    );
  }

  if (screen === "refused") {
    return (
      <div className="sudoku-screen sudoku-screen--center" data-testid="sudoku-refused">
        <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
          <p style={{ color: "var(--danger)", margin: 0, fontWeight: 700 }}>{refusal}</p>
          <Button variant="primary" onClick={toLobby}>
            {fa.sudoku.leave}
          </Button>
        </div>
      </div>
    );
  }

  const classic = overview?.classic ?? null;
  const today = overview?.daily ?? null;
  /** The record at the chosen size and level (null: none yet). */
  const best = overview?.records[`${size}-${level}` as SudokuRecordKey] ?? null;
  const dailyDone = today !== null && today.finishedMs !== null;
  /** Today's daily was lost to its 3rd mistake: over for today. */
  const dailyLost = today !== null && today.outcome === "lost";
  // The daily's size and level: today's game's own once started, else the engine's (the server's).
  const dailyPuzzle = `${fa.sudoku.sizes[today?.size ?? DAILY_SIZE]} ${
    fa.sudoku.levels[today?.level ?? DAILY_LEVEL]
  }`;

  const cards = (
    <>
      {classic ? (
        <button
          type="button"
          className={`${CARD} ${CARD_TAP}`}
          onClick={() => play(classic)}
          data-testid="sudoku-continue"
        >
          <span className="sudoku-card__text">
            <span className="sudoku-card__label">{fa.sudoku.continueTitle}</span>
            <span>
              {fa.sudoku.sizes[classic.size]} {fa.sudoku.levels[classic.level]}{" "}
              <span className="whitespace-nowrap">
                · <span className="tabular-nums">{time(classic.state.elapsedMs)}</span>
              </span>
            </span>
          </span>
          <span className={BADGE}>{fa.sudoku.continueAction}</span>
        </button>
      ) : null}
      {dailyLost ? (
        <div className={CARD} data-testid="sudoku-daily">
          <span className="sudoku-card__text">
            <span className="sudoku-card__label">
              {fa.sudoku.daily} · {fa.sudoku.dailyTomorrow}
            </span>
            <span style={{ color: "var(--danger)" }} data-testid="sudoku-daily-lost">
              {dailyPuzzle} · <span className="whitespace-nowrap">{fa.sudoku.dailyLost}</span>
            </span>
          </span>
        </div>
      ) : dailyDone ? (
        <div className={CARD} data-testid="sudoku-daily">
          <span className="sudoku-card__text">
            <span className="sudoku-card__label">{fa.sudoku.daily}</span>
            <span className="text-secondary" data-testid="sudoku-daily-done">
              {dailyPuzzle} · ✓ {fa.sudoku.dailySolved}{" "}
              <span className="whitespace-nowrap">
                · <span className="tabular-nums">{time(today.finishedMs ?? 0)}</span>
              </span>
            </span>
          </span>
        </div>
      ) : (
        <button
          type="button"
          className={`${CARD} ${CARD_TAP}`}
          onClick={daily}
          disabled={!overview || busy}
          data-testid="sudoku-daily"
        >
          <span className="sudoku-card__text">
            <span className="sudoku-card__label">{fa.sudoku.daily}</span>
            <span>
              {dailyPuzzle}
              {startFailed === "daily" ? (
                <>
                  {" "}
                  <span className="whitespace-nowrap">
                    ·{" "}
                    <span style={{ color: "var(--danger)" }} data-testid="sudoku-daily-failed">
                      {fa.sudoku.dailyFailed}
                    </span>
                  </span>
                </>
              ) : today ? (
                <>
                  {" "}
                  <span className="whitespace-nowrap">
                    · <span className="tabular-nums">{time(today.state.elapsedMs)}</span>
                  </span>
                </>
              ) : null}
            </span>
          </span>
          <span className={BADGE}>
            {startFailed === "daily"
              ? fa.sudoku.tryAgain
              : today
                ? fa.sudoku.dailyContinue
                : fa.sudoku.dailyStart}
          </span>
        </button>
      )}
    </>
  );

  return (
    <>
      <StartScreenShell
        title={fa.sudoku.title}
        emoji="🔢"
        heading={fa.sudoku.newTitle}
        leaveLabel={fa.sudoku.leave}
        startLabel={startFailed === "new" ? fa.sudoku.startFailed : fa.sudoku.start}
        onStart={() => {
          if (!overview || busy) return;
          if (classic) setAsking(true);
          else newGame(null);
        }}
        onLeave={toLobby}
        before={cards}
      >
        {failed ? (
          <div className="sudoku-start-line" data-testid="sudoku-load-failed">
            <span>{fa.sudoku.loadFailed}</span>
            <Button variant="ghost" onClick={load}>
              {fa.sudoku.retry}
            </Button>
          </div>
        ) : null}
        <div className="sudoku-record" data-testid="sudoku-record">
          <span className="sudoku-record__label">
            🏆 {fa.sudoku.record} {fa.sudoku.sizes[size]} {fa.sudoku.levels[level]}
          </span>
          {overview === null ? (
            // Not read yet (or the read failed): no claim either way — «none yet» could be false.
            <span className="sudoku-record__none" data-testid="sudoku-record-unknown">
              {fa.sudoku.recordUnknown}
            </span>
          ) : best === null ? (
            <span className="sudoku-record__none" data-testid="sudoku-record-none">
              {fa.sudoku.noRecordYet}
            </span>
          ) : (
            <span
              className="font-numeral-xl text-numeral-xl tabular-nums text-secondary"
              data-testid="sudoku-record-time"
            >
              {time(best)}
            </span>
          )}
        </div>
        <div className="sudoku-size-row">
          <SegmentedChoice
            label={fa.sudoku.size}
            options={SIZE_OPTIONS}
            value={size}
            onChange={setSize}
            compact
            className="min-w-0 flex-1"
          />
          <div className="sudoku-check" data-testid="sudoku-check">
            <span className="sudoku-check__label" aria-hidden>
              {fa.sudoku.check}
            </span>
            <Toggle
              checked={mode === "check"}
              onChange={() => changeMode(mode !== "check")}
              label={fa.sudoku.check}
            />
          </div>
        </div>
        <SegmentedChoice
          label={fa.sudoku.level}
          options={LEVEL_OPTIONS}
          value={level}
          onChange={setLevel}
          compact
          className="w-full"
        />
      </StartScreenShell>
      {asking && classic ? (
        <ConfirmDialog
          title={fa.sudoku.replaceTitle}
          body={fa.sudoku.replaceBody}
          confirmLabel={fa.sudoku.replaceConfirm}
          cancelLabel={fa.sudoku.replaceCancel}
          onConfirm={() => {
            setAsking(false);
            newGame(classic.rev);
          }}
          onCancel={() => setAsking(false)}
        />
      ) : null}
    </>
  );
}
